import { css } from '@emotion/react';
import { ProgressSpinner } from 'folio-common-components';
import * as React from 'react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DenseProgressList } from '../../components/DenseProgressList';
import { PageCard } from '../../components/Section';
import { AltinnStep } from '../../components/Steps/Altinn';
import { BrregStep } from '../../components/Steps/Brreg';
import { DoneStep } from '../../components/Steps/Done';
import { FikenStep } from '../../components/Steps/Fiken';
import { SignStep } from '../../components/Steps/Sign';
import { SignAllStep } from '../../components/Steps/SignAll';
import { ChooseBankStep } from '../../components/Steps/choose-bank';
import { GoToOnboardingStep } from '../../components/Steps/continue-onboarding';
import { OtherBankAllSigned } from '../../components/Steps/done-non-folio';
import { OtherBankStep } from '../../components/Steps/other-bank';
import {
  OtherBank,
  OtherBankUploadBankReceiptStep,
} from '../../components/Steps/other-bank-nagged';
import { PayToFolioStep } from '../../components/Steps/pay-to-folio';
import type { MaBankSelection } from '../../gqltypes';
import { pageMap } from '../../paths';
import { type RootState, useActions, useSelector } from '../../state/';
import {
  selectors as foundingSelectors,
  selectors,
} from '../../state/founding';

type Progress = ReturnType<typeof foundingSelectors.foundingProgress>;

function getAwaitOpenAccountComponent(bankSelection: MaBankSelection | null) {
  // TODO: this can be moved
  if (bankSelection === null) {
    bankSelection = 'UNSET';
  }

  switch (bankSelection) {
    case 'UNSET':
      return <ChooseBankStep />;
    case 'FOLIO':
    case 'FOLIO_AML_COMPLETE':
    case 'FOLIO_ONBOARDED':
      return <GoToOnboardingStep />;
    case 'OTHER':
      return <OtherBankStep />;
    case 'OTHER_NAGGED':
      return <OtherBank />;
    case 'FOLIO_REJECTED':
      // todo: Should we show something else here as well?
      return <OtherBank />;
    default:
      throw new Error('This should not happen');
  }
}

function stepComponent(
  progress: Progress,
  bankSelection: MaBankSelection | null,
) {
  switch (progress) {
    case 'open':
    case 'submitted':
      return <div />; // should never happen
    case 'await_signing':
      return <SignStep />;
    case 'await_open_account':
      return getAwaitOpenAccountComponent(bankSelection);
    case 'await_all_signing':
      return <SignAllStep />;
    case 'await_payment_documentation':
      return <PayToFolioStep />;
    case 'await_altinn_signing':
      return <AltinnStep />;
    case 'accounting_system_selection':
      return <FikenStep />;
    case 'await_brreg':
      return <BrregStep />;
    case 'finished': {
      if (bankSelection === 'OTHER_NAGGED') {
        return <OtherBankAllSigned />;
      } else {
        return <DoneStep />;
      }
    }
  }
}

function useShouldForceUpload() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  return searchParams.get('forceUpload') === 'true';
}

function selector(state: RootState) {
  const signers = selectors.signings(state);
  const signer = signers.find(signer => signer.id === state.editor.formOwner);
  return {
    foundingId: state.editor.id,
    progress: foundingSelectors.foundingProgress(state),
    bankSelection: state.founding.bankSelection,
    signingUrl: signer ? signer.url : undefined,
  };
}

const ProgressPage: React.FC = () => {
  const { progress, bankSelection, signingUrl, foundingId } =
    useSelector(selector);

  const navigate = useNavigate();
  const { foundingThunks } = useActions();
  const forceUpload = useShouldForceUpload();

  useEffect(() => {
    const timerId = setInterval(foundingThunks.updateProgressThunk, 2000);
    return () => {
      clearInterval(timerId);
    };
  }, [foundingThunks]);

  useEffect(() => {
    if (progress === 'open') {
      navigate(
        {
          pathname: pageMap.step5,
          search: `?id=${foundingId}`,
        },
        { replace: true },
      );
    }
  }, [foundingId, navigate, progress]);

  return progress === 'submitted' ||
    // Signing URL is required to render the first page on /status
    signingUrl === undefined ? (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
      `}
    >
      <ProgressSpinner size={48} />
      <p>Jobber…</p>
    </div>
  ) : (
    <PageCard>
      {forceUpload ? (
        // TODO: this can be removed when SUS is fully automated
        <OtherBankUploadBankReceiptStep />
      ) : (
        stepComponent(progress, bankSelection)
      )}

      <DenseProgressList />
    </PageCard>
  );
};

// React.lazy only supports default exports:
// https://reactjs.org/docs/code-splitting.html#named-exports
// eslint-disable-next-line import/no-default-export
export default ProgressPage;
